import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorCode, errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { WBSButton } from '@/common/WBSButton';
import { BorderedRichTextEditor } from '@/messages/BorderedRichTextEditor';
import { useRichTextEditor } from '~/hooks/useRichTextEditor';
import { ModalParagraph, StudentBoardModal } from './StudentBoardModal';

interface EncourageStudentModalProps {
  /** The student to encourage. `null` renders nothing — this is the open switch. */
  studentId: number | null;
  /**
   * The board row's own name. Encourage has no `/adoption-info` step to read it
   * from — that endpoint runs the ADOPT ladder and would refuse the modal for a
   * helper who may encourage but not adopt — so the name comes from the row
   * that was clicked.
   */
  studentName: string;
  onClose: () => void;
  /** Fired once the encouragement has landed, so the board can refresh behind it. */
  onEncouraged?: () => void;
}

/**
 * Legacy's two-step encourage popup: `verify()` renders the message form and
 * `send()` replaces it with the sent body — `EncourageStudentInteractivePopup.php:15-52`,
 * popups 21908 and 21909.
 *
 * The field is a RICH TEXT editor, not a textarea. `encourage_form.php:16`
 * renders a `<textarea>` and `:19-27` immediately upgrades it with
 * `trumbowyg({btns: [['bold','italic','underline','blockquote']]})`, which is
 * why the stored body is HTML and why legacy's own 1500 rule is
 * `strlen(utf8_decode(strip_tags($message)))` (`:61`) rather than a `maxlength`.
 * There is no character counter and no client-side length check for the same
 * reason: legacy has neither, and the limit is a plain-text length the browser
 * cannot measure off the markup.
 *
 * A refusal re-renders the FORM with a notice above it rather than replacing the
 * body (`validate_encouragement_message` calls `verify($error, $message)`), so
 * the typed message survives a rejection.
 */
export function EncourageStudentModal({
  studentId,
  studentName,
  onClose,
  onEncouraged,
}: EncourageStudentModalProps) {
  const { t } = useTranslation();
  const editor = useRichTextEditor();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [showEmptyError, setShowEmptyError] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [openedFor, setOpenedFor] = useState<number | null>(studentId);
  // Which student the modal is showing RIGHT NOW, readable from a settled
  // request — see AdoptStudentModal for why a settled POST must not write its
  // success body into whatever modal is open by then.
  const openSession = useRef<number | null>(studentId);
  // Synchronous single-flight; `isPending` disables the button a render late,
  // and a second send would come back 409 `student_already_encouraged`.
  const submitting = useRef(false);

  const { mutateAsync: sendEncouragement, isPending } =
    api.sendEncouragement.useMutation();
  const submitErrorMessage = useEncouragementErrorMessage(submitError);

  // Reopening for a different student must not inherit the last one's sent body
  // or error. Adjusting state during render (rather than in an effect) keeps the
  // first paint of the new modal correct.
  if (openedFor !== studentId) {
    setOpenedFor(studentId);
    setSentTo(null);
    setShowEmptyError(false);
    setSubmitError(null);
  }

  useEffect(() => {
    openSession.current = studentId;
  }, [studentId]);

  if (studentId === null) return null;

  const handleClose = (): void => {
    editor.clearEditor();
    // The student STAYS on the board after an encouragement — unlike adopt and
    // pass-on — so the same row can be clicked again before the board refetches.
    // Without this reset that second open would paint the previous sent body.
    setSentTo(null);
    setShowEmptyError(false);
    setSubmitError(null);
    onClose();
  };

  // Sent first: the encouragement has landed, so nothing that follows can undo
  // it. Held as the name it was sent to rather than a bool, so the sentence
  // still names the right student if the row list refreshes underneath.
  if (sentTo !== null) {
    return (
      <StudentBoardModal
        title={t('wbs.student-encouraged')}
        onClose={handleClose}
        footer={
          <WBSButton alternate onClick={handleClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>
          {t('wbs.your-encouragement-has-been-sent', { name: sentTo })}
        </ModalParagraph>
      </StudentBoardModal>
    );
  }

  const handleSend = async (): Promise<void> => {
    if (submitting.current) return;
    // The plain-text form of the field, which is what legacy's `trim($message)`
    // sees: a contenteditable that has only been focused holds
    // `<div><br></div>`, so an emptiness test on the markup passes an empty
    // message straight to the server.
    if (!editor.getEditorContent()) {
      setShowEmptyError(true);
      setSubmitError(null);
      return;
    }
    submitting.current = true;
    const submittedFor = studentId;
    const submittedName = studentName;
    setShowEmptyError(false);
    setSubmitError(null);
    try {
      await sendEncouragement({
        body: {
          student_id: studentId,
          // The MARKUP, not the text: bold/italic/underline/blockquote are the
          // point of the control, and the server stores the display-formatted
          // body (`canned_responses/utils.py:125 display_content`).
          message: editor.editorRef.current?.innerHTML ?? '',
        },
      });
    } catch (failed) {
      if (openSession.current === submittedFor) setSubmitError(failed);
      return;
    } finally {
      submitting.current = false;
    }
    // The write landed either way, so the board is stale either way; only the
    // modal body is conditional on this still being the student on screen.
    onEncouraged?.();
    if (openSession.current === submittedFor) setSentTo(submittedName);
  };

  return (
    <StudentBoardModal
      title={t('wbs.encourage-this-student')}
      onClose={handleClose}
      footer={
        <WBSButton
          onClick={() => {
            void handleSend();
          }}
          isLoading={isPending}
        >
          {t('wbs.send')}
        </WBSButton>
      }
    >
      {showEmptyError && (
        <AlertBox variant="error">
          {t('wbs.please-enter-an-encouragement')}
        </AlertBox>
      )}
      {submitErrorMessage !== null && (
        <AlertBox variant="error">{submitErrorMessage}</AlertBox>
      )}
      <FieldLabel htmlFor="encouragement_message">
        {t('wbs.message-of-encouragement')}
      </FieldLabel>
      {/* Legacy's own field id, and the toolbar is its Trumbowyg button set
          verbatim (`encourage_form.php:16,20`). */}
      <BorderedRichTextEditor
        editor={editor}
        id="encouragement_message"
        visibleTools={['bold', 'italic', 'underline', 'blockquote']}
      />
    </StudentBoardModal>
  );
}

/**
 * The server's refusals, in the words legacy uses for the two it also has.
 *
 * `encouragement_message_empty` and `encouragement_message_too_long` are
 * `validate_encouragement_message`'s two notices (`:55-63`); the two 409s are
 * the button's own disabled states rendered as prose, which is what a helper
 * gets when the row's label was computed before someone else's send landed
 * (`EncouragementModel.php:19-29`).
 */
function useEncouragementErrorMessage(error: unknown): string | null {
  const { t } = useTranslation();

  if (error === null) return null;

  switch (errorCode(error)) {
    case 'encouragement_message_empty':
      return t('wbs.please-enter-an-encouragement');
    case 'encouragement_message_too_long':
      return t('wbs.encourage-message-cannot-be');
    case 'student_already_encouraged':
      return t('profile.you-already-sent-encouragement');
    case 'student_fully_encouraged':
      return t('wbs.maximum-encouragement-limit-reached');
    case 'student_already_adopted':
      return t('wbs.sorry-this-student-has-already-been-adopted');
    default:
      // `student_not_encourageable` and the structural 404 have no legacy
      // popup — legacy hides the button instead of wording a refusal — so they
      // land on the generic message rather than on an invented string.
      return errorMessage(error, t('wbs.something-went-wrong-please'));
  }
}

const FieldLabel = styled.label`
  color: var(--grey-900);
  display: block;
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 8px 0;
`;
